import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Container,
  Badge,
  Spinner,
  ListGroup,
  ListGroupItem,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import EventService from "../../../services/KangarooServices/EventService";
import { useNavigate, useParams } from "react-router-dom";
import { imageBaseApi } from "../../../utilis/imageHelper";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  Edit3,
  Calendar,
  MapPin,
  User,
  Globe,
  CheckCircle,
  XCircle,
  ExternalLink,
  Tag,
  Phone,
  Image as ImageIcon,
  Info,
} from "lucide-react";
import { Triangle } from "react-loader-spinner";
import { formatDateTime } from "../../../Components/Common/FormatDate";

const EventDetail = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMainTab, setActiveMainTab] = useState("1");
  const { id } = useParams();
  const navigate = useNavigate();

  const toggleMainTab = (tab) => {
    if (activeMainTab !== tab) setActiveMainTab(tab);
  };

  const fetchEventDetail = async (id) => {
    try {
      setLoading(true);
      console.log("Fetching ID:", id);
      const response = await EventService.getBySlug(id);
      console.log("Response received:", response);

      setData(response);
    } catch (err) {
      console.error("Failed to fetch event detail:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchEventDetail(id);
    }
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const breadcrumbItems = [{ title: "Back to List", link: "/event" }];

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="my-3 d-flex justify-content-center align-items-center">
            <Triangle height="100" width="100" color="#556ee6" />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Event Detail"
          pageTitle="Event"
          breadcrumbItems={breadcrumbItems}
        />

        {/* Hero Section with Banner */}
        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-sm mb-4 overflow-hidden">
              {data?.bannerMedia?.url ? (
                <div className="position-relative bg-dark">
                  <img
                    src={`${imageBaseApi}${data.bannerMedia.url}`}
                    alt={data?.name}
                    className="w-100 opacity-50"
                    style={{ height: "250px", objectFit: "cover" }}
                  />
                  <div className="position-absolute top-0 end-0 m-3 m-md-4">
                    <button
                      type="button"
                      className="btn btn-success btn-sm d-flex align-items-center gap-2"
                      onClick={() => navigate(`/event/update/${id}`)}
                    >
                      <Edit3 size={14} />
                      <span className="d-none d-sm-inline">Update</span>
                    </button>
                  </div>
                  <div className="position-absolute bottom-0 start-0 end-0 p-3 p-md-4 text-white">
                    <div className="d-flex flex-column gap-2">
                      <Badge
                        color="primary"
                        className="px-3 py-2 fs-6 align-self-start"
                      >
                        <Tag size={14} className="me-1" />
                        <span
                          className="text-truncate d-inline-block"
                          style={{ maxWidth: "200px" }}
                        >
                          {data?.eventType}
                        </span>
                      </Badge>
                      <h1 className="display-6 fw-bold mb-0 text-white text-break pe-5">
                        {data?.name}
                      </h1>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-primary-subtle">
                  <CardBody className="p-3 p-md-4">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
                      <div className="flex-grow-1 w-100 overflow-hidden">
                        <Badge color="primary" className="mb-3 px-3 py-2 fs-6">
                          <Tag size={14} className="me-1" />
                          <span
                            className="text-truncate d-inline-block"
                            style={{ maxWidth: "250px" }}
                          >
                            {data?.eventType}
                          </span>
                        </Badge>
                        <h1 className="display-6 fw-bold mb-0 text-break">
                          {data?.name}
                        </h1>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm d-flex align-items-center gap-2 flex-shrink-0"
                        onClick={() => navigate(`/event/update/${id}`)}
                      >
                        <Edit3 size={14} />
                        Update
                      </button>
                    </div>
                  </CardBody>
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {/* Main Tabs */}
        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-sm">
              <CardBody className="p-0">
                {/* Main Tab Navigation */}
                <Nav tabs className="nav-tabs-custom border-bottom">
                  <NavItem>
                    <NavLink
                      className={`${
                        activeMainTab === "1" ? "active" : ""
                      } cursor-pointer py-2 d-flex align-items-center flex-nowrap`}
                      onClick={() => toggleMainTab("1")}
                    >
                      <Info size={16} className="me-1" />
                      <span className="fw-semibold">Event Information</span>
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={`${
                        activeMainTab === "2" ? "active" : ""
                      } cursor-pointer py-2 d-flex align-items-center flex-nowrap`}
                      onClick={() => toggleMainTab("2")}
                    >
                      <MapPin size={16} className="me-1" />
                      <span className="fw-semibold">
                        Cities & Locations
                        {data?.eventsCities && data.eventsCities.length > 0 && (
                          <Badge color="primary" className="ms-1">
                            {data.eventsCities.length}
                          </Badge>
                        )}
                      </span>
                    </NavLink>
                  </NavItem>
                </Nav>

                {/* Main Tab Content */}
                <TabContent activeTab={activeMainTab}>
                  {/* Tab 1: Event Information */}
                  <TabPane tabId="1">
                    <div className="p-4">
                      <Row>
                        {/* Main Content */}
                        <Col lg={8}>
                          {/* Event Date & Time */}
                          <Card className="border shadow-sm mb-4">
                            <CardBody className="">
                              <CardHeader className="fw-bold border-0 p-3 border-bottom mb-2 text-primary">
                                <Calendar size={20} className="me-2" />
                                Event Schedule
                              </CardHeader>
                              <Row className="g-4">
                                <Col md={6}>
                                  <div className="border-start border-success border-4 ps-3">
                                    <div className="text-muted small mb-1">
                                      Start Date & Time
                                    </div>
                                    <div className="fw-semibold text-success">
                                      {formatDate(data?.startDate)}
                                    </div>
                                  </div>
                                </Col>
                                <Col md={6}>
                                  <div className="border-start border-danger border-4 ps-3">
                                    <div className="text-muted small mb-1">
                                      End Date & Time
                                    </div>
                                    <div className="fw-semibold text-danger">
                                      {formatDate(data?.endDate)}
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                            </CardBody>
                          </Card>

                          {/* Event Description */}
                          <Card className="border shadow-sm mb-4">
                            <CardBody className="p-4">
                              <h5 className="fw-bold mb-3 text-primary">
                                Event Description
                              </h5>
                              <p className="fs-6 lh-lg text-secondary mb-0">
                                {data?.description}
                              </p>
                            </CardBody>
                          </Card>

                          {/* Event Details Grid */}
                          <Card className="border shadow-sm">
                            <CardBody className="p-4">
                              <h5 className="fw-bold mb-4 text-primary">
                                Additional Information
                              </h5>
                              <Row className="g-4">
                                <Col md={6}>
                                  <div className="d-flex align-items-start gap-3">
                                    <div className="bg-primary bg-opacity-10 rounded p-2 flex-shrink-0">
                                      <MapPin
                                        size={20}
                                        className="text-primary"
                                      />
                                    </div>
                                    <div className="flex-grow-1 overflow-hidden">
                                      <div className="text-muted small mb-1">
                                        Venue
                                      </div>
                                      <div className="fw-semibold text-break">
                                        {data?.venue}
                                      </div>
                                    </div>
                                  </div>
                                </Col>

                                <Col md={6}>
                                  <div className="d-flex align-items-start gap-3">
                                    <div className="bg-primary bg-opacity-10 rounded p-2 flex-shrink-0">
                                      <User
                                        size={20}
                                        className="text-primary"
                                      />
                                    </div>
                                    <div className="flex-grow-1 overflow-hidden">
                                      <div className="text-muted small mb-1">
                                        Speaker
                                      </div>
                                      <div className="fw-semibold text-break">
                                        {data?.speaker}
                                      </div>
                                    </div>
                                  </div>
                                </Col>

                                <Col md={6}>
                                  <div className="d-flex align-items-start gap-3">
                                    <div className="bg-primary bg-opacity-10 rounded p-2 flex-shrink-0">
                                      <Tag size={20} className="text-primary" />
                                    </div>
                                    <div className="flex-grow-1">
                                      <div className="text-muted small mb-1">
                                        Event Type
                                      </div>
                                      <Badge
                                        color="primary"
                                        className="px-3 py-2 fs-6"
                                      >
                                        {data?.eventType}
                                      </Badge>
                                    </div>
                                  </div>
                                </Col>

                                <Col md={6}>
                                  <div className="d-flex align-items-start gap-3">
                                    <div
                                      className={`${
                                        data?.registrationRequired
                                          ? "bg-success"
                                          : "bg-danger"
                                      } bg-opacity-10 rounded p-2 flex-shrink-0`}
                                    >
                                      {data?.registrationRequired ? (
                                        <CheckCircle
                                          size={20}
                                          className="text-success"
                                        />
                                      ) : (
                                        <XCircle
                                          size={20}
                                          className="text-danger"
                                        />
                                      )}
                                    </div>
                                    <div className="flex-grow-1">
                                      <div className="text-muted small mb-1">
                                        Registration Required
                                      </div>
                                      <Badge
                                        color={
                                          data?.registrationRequired
                                            ? "success"
                                            : "danger"
                                        }
                                        className="px-3 py-2 fs-6"
                                      >
                                        {data?.registrationRequired
                                          ? "Yes"
                                          : "No"}
                                      </Badge>
                                    </div>
                                  </div>
                                </Col>

                                {data?.registrationUrl && (
                                  <Col md={12}>
                                    <div className="d-flex align-items-start gap-3">
                                      <div className="bg-info bg-opacity-10 rounded p-2 flex-shrink-0">
                                        <ExternalLink
                                          size={20}
                                          className="text-info"
                                        />
                                      </div>
                                      <div className="flex-grow-1 overflow-hidden">
                                        <div className="text-muted small mb-1">
                                          Registration URL
                                        </div>
                                        <a
                                          href={
                                            data.registrationUrl.startsWith(
                                              "http"
                                            )
                                              ? data.registrationUrl
                                              : `https://${data.registrationUrl}`
                                          }
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary fw-semibold text-decoration-none d-flex align-items-center gap-2"
                                        >
                                          <span className="text-break">
                                            Register Now
                                          </span>
                                          <ExternalLink
                                            size={16}
                                            className="flex-shrink-0"
                                          />
                                        </a>
                                      </div>
                                    </div>
                                  </Col>
                                )}
                              </Row>
                            </CardBody>
                          </Card>
                        </Col>

                        {/* Sidebar */}
                        <Col lg={4}>
                          <Card className="border shadow-sm mb-4 overflow-hidden">
                            <CardHeader className="bg-transparent border-bottom py-3">
                              <h5 className="mb-0 d-flex align-items-center gap-2 text-dark">
                                <ImageIcon size={18} className="text-primary" />
                                Banner Image
                              </h5>
                            </CardHeader>

                            <CardBody className="p-0">
                              {data?.thumbnailMedia?.url ? (
                                <>
                                  <div className="bg-light d-flex justify-content-center">
                                    <img
                                      src={`${imageBaseApi}${data.thumbnailMedia.url}`}
                                      alt={data?.name}
                                      className="img-fluid w-100"
                                      style={{
                                        maxHeight: "250px",
                                        objectFit: "contain",
                                      }}
                                    />
                                  </div>

                                  {data.thumbnailMedia.fileName && (
                                    <div className="p-3 bg-white">
                                      <div className="d-flex align-items-center gap-2 text-muted small">
                                        <Tag
                                          size={12}
                                          className="text-primary flex-shrink-0"
                                        />
                                        <span className="text-truncate">
                                          {data.thumbnailMedia.fileName}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div
                                  className="d-flex flex-column justify-content-center align-items-center text-center bg-light"
                                  style={{ height: "250px" }}
                                >
                                  <ImageIcon
                                    size={48}
                                    className="text-muted mb-2"
                                  />
                                  <h6 className="mb-1 text-muted">
                                    No Banner Image
                                  </h6>
                                  <p className="small text-muted mb-0">
                                    Banner image not uploaded for this event
                                  </p>
                                </div>
                              )}
                            </CardBody>
                          </Card>

                          {/* Target Country */}
                          {data?.targetCountry && (
                            <Card className="border shadow-sm mb-4">
                              <CardBody className="p-4">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                  <Globe size={20} className="text-primary" />
                                  <h5 className="fw-bold mb-0">
                                    Target Country
                                  </h5>
                                </div>
                                <Badge
                                  color="info"
                                  className="mb-3 px-3 py-2 fs-6"
                                >
                                  {data.targetCountry.name}
                                </Badge>
                                {data.targetCountry.description && (
                                  <div
                                    className="text-muted small lh-base"
                                    dangerouslySetInnerHTML={{
                                      __html: data.targetCountry.description,
                                    }}
                                  />
                                )}
                              </CardBody>
                            </Card>
                          )}
                        </Col>
                      </Row>
                    </div>
                  </TabPane>

                  {/* Tab 2: Cities & Locations */}
                  <TabPane tabId="2">
                    <div className="p-4">
                      {data?.eventsCities && data.eventsCities.length > 0 ? (
                        <Row className="g-4">
                          {data.eventsCities.map((city, index) => (
                            <Col lg={3} key={index}>
                              <Card className="border shadow-sm h-100">
                                <CardHeader className="bg-primary bg-opacity-10 border-bottom">
                                  <div className="d-flex align-items-center justify-content-between">
                                    <h5 className="mb-0 d-flex align-items-center gap-2 text-primary">
                                      <MapPin size={20} />
                                      {city.city}
                                    </h5>
                                  </div>
                                </CardHeader>

                                <CardBody className="p-0">
                                  {/* City Image */}
                                  {city.eventImage && (
                                    <div className="bg-light">
                                      <img
                                        src={`${imageBaseApi}${city.eventImage.url}`}
                                        alt={city.city}
                                        className="img-fluid w-100"
                                        style={{
                                          height: "200px",
                                          objectFit: "cover",
                                        }}
                                      />
                                    </div>
                                  )}

                                  {/* City Details */}
                                  <div className="p-4">
                                    <Row className="g-3">
                                      {/* Date */}
                                      {city.eventDate && (
                                        <Col xs={12}>
                                          <div className="d-flex align-items-start gap-3">
                                            <div className="bg-success bg-opacity-10 rounded p-2 flex-shrink-0">
                                              <Calendar
                                                size={18}
                                                className="text-success"
                                              />
                                            </div>
                                            <div className="flex-grow-1">
                                              <div className="text-muted small mb-1">
                                                Event Date
                                              </div>
                                              <div className="fw-semibold">
                                                {formatDateTime(city.eventDate)}
                                              </div>
                                            </div>
                                          </div>
                                        </Col>
                                      )}

                                      {/* Location */}

                                      <Col xs={12}>
                                        <div className="d-flex align-items-start gap-3">
                                          <div className="bg-info bg-opacity-10 rounded p-2 flex-shrink-0">
                                            <MapPin
                                              size={18}
                                              className="text-info"
                                            />
                                          </div>
                                          <div className="flex-grow-1">
                                            <div className="text-muted small mb-1">
                                              Location
                                            </div>
                                            <div className="fw-medium text-break">
                                              {city.location}
                                            </div>
                                          </div>
                                        </div>
                                      </Col>

                                      {/* Phone Number */}

                                      <Col xs={12}>
                                        <div className="d-flex align-items-start gap-3">
                                          <div className="bg-warning bg-opacity-10 rounded p-2 flex-shrink-0">
                                            <Phone
                                              size={18}
                                              className="text-warning"
                                            />
                                          </div>
                                          <div className="flex-grow-1">
                                            <div className="text-muted small mb-1">
                                              Contact Phone
                                            </div>
                                            <a
                                              href={`tel:${city.phoneNumber}`}
                                              className="fw-medium text-decoration-none d-flex align-items-center gap-2"
                                            >
                                              <Phone size={14} />
                                              {city.phoneNumber}
                                            </a>
                                          </div>
                                        </div>
                                      </Col>
                                    </Row>
                                  </div>
                                </CardBody>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      ) : (
                        <div className="text-center py-5">
                          <MapPin size={64} className="text-muted mb-3" />
                          <h5 className="text-muted mb-2">
                            No Event Cities Available
                          </h5>
                          <p className="text-muted mb-0">
                            No other event cities or locations have been added
                            yet.
                          </p>
                        </div>
                      )}
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EventDetail;
